// Home's requisition cards (DC-53, H-11 "Requisition cards carry health and readiness, not completion; counts per
// DC-31."): one row per live requisition in the reader's scope, its status word, Req health (number, band word), Search
// Readiness % and the candidates in each of his seven board columns — Sourced · Qualified · In HM Review · Recruiter
// screen · HM interview · Final Interview Loop · Offer. Health and readiness come from S19's batched requisition.signals
// (with the drivers and the missing-prerequisite hint) once it answers, and from S13's widget data until then or where
// the reader may not call it. The HM's own open reqs (B1's `own_open_reqs`) draw the same card (D-25).
//
// D-25 (HO-20, HO-21; home family call 11): one card per requisition (a Card2 inside the widget), and, where the row
// carries them, the requisition timeline's compact strip on DC-30's seven stations (only the active station's state:
// "The compact `tl.sm` on Home cards shows only the active-station state.") and the foot line "Days open {calendar} ·
// Active Time {active} · Days on Hold {hold}" (BRD §7 "Days open, active days, hold days"). B1's own_open_reqs rows carry
// both; a row with neither draws the card without them.

import type { ReactNode } from "react";
import {
  Card2, Flex, Health, MilestoneTimeline, Paragraph, Pill, Ring, Row, RowName, Stack, Stat, Widget, type HealthDriver, type MilestoneStation,
} from "@/mc";
import { METRIC_LABELS } from "@/mc/g3/labels";
import { HISTORY_LABELS, MILESTONE_LABELS } from "@/mc/g5/labels";
import { G6_COLUMN_LABELS } from "@/mc/g6/labels";
import { NAV_ITEM_LABELS, SLA_LABELS } from "@/mc/labels";
import { RowSignals, hsKey, useSignals } from "@/views/b10/lists/RowSignals";
import type { HomeWidgetView } from "./host";
import type { SignalRow } from "@/api/tools";
import { DASH, activeRequisitionsOf, figure, seatsOf, type ActiveRequisitionRow } from "./api";
import { HOME_TEMPLATES, REQUISITION_STATUS_WORDS } from "./labels";

/** DC-31's columns in board order: the backend key (reporting/applications.COLUMNS) and his word. */
export const STAGE_COLUMNS: readonly (readonly [string, string])[] = [
  ["sourced", G6_COLUMN_LABELS.sourced],
  ["qualified", G6_COLUMN_LABELS.qualified],
  ["in_hm_review", G6_COLUMN_LABELS.inHmReview],
  ["recruiter_screen", G6_COLUMN_LABELS.recruiterScreen],
  ["hm_interview", G6_COLUMN_LABELS.hmInterview],
  ["final_interview_loop", G6_COLUMN_LABELS.finalInterviewLoop],
  ["offer", G6_COLUMN_LABELS.offer],
];

/** DC-30's stations, in his order (Request · Approval · Posting · Intake · Panel · Offer · Filled). */
export const STATION_WORDS: readonly (readonly [string, string])[] = [
  ["request", MILESTONE_LABELS.request],
  ["approval", MILESTONE_LABELS.approval],
  ["posting", MILESTONE_LABELS.posting],
  ["intake", MILESTONE_LABELS.intake],
  ["panel", MILESTONE_LABELS.panel],
  ["offer", MILESTONE_LABELS.offer],
  ["filled", MILESTONE_LABELS.filled],
];

const STATES: ReadonlySet<string> = new Set(["done", "active", "hold", "next", "skip"]);

/** A row's `stations` ([{key, state}], the server's reading of the record) as the strip's stations, in DC-30's order;
 * none when the row carries no stations. */
export function stationsOf(value: unknown): MilestoneStation[] {
  if (!Array.isArray(value)) return [];
  const state = new Map(value.map((item: { key?: unknown; state?: unknown } | null) => [String(item?.key), String(item?.state)] as const));
  if (!STATION_WORDS.some(([key]) => state.has(key))) return [];
  return STATION_WORDS.map(([key, label]) => {
    const at = state.get(key);
    return { label, state: at !== undefined && STATES.has(at) ? (at as MilestoneStation["state"]) : "next" };
  });
}

/** Days open as a row carries them: Calendar Time, Active Time and Days on Hold (none before it opens). */
export interface DaysOpen {
  calendar_days?: number | null | undefined;
  active_days?: number | null | undefined;
  hold_days?: number | null | undefined;
}

const dayCount = (value: unknown): number | null => (typeof value === "number" && Number.isFinite(value) ? value : null);

/** A row's Days open, read loosely from its data. */
export function daysOf(row: unknown): DaysOpen {
  const loose = (row && typeof row === "object" ? row : {}) as Record<string, unknown>;
  return { calendar_days: dayCount(loose.calendar_days), active_days: dayCount(loose.active_days), hold_days: dayCount(loose.hold_days) };
}

/** The card's foot line: "Days open {calendar} · Active Time {active} · Days on Hold {hold}" (BRD §7 "Days open, active
 * days, hold days"; G §14 "Report Calendar Time, Active Time and Days on Hold."), or nothing before the requisition opens.
 * One string, so it reads as one line. */
export function daysLine({ calendar_days: calendar, active_days: active, hold_days: hold }: DaysOpen): string | undefined {
  if (calendar == null || active == null || hold == null) return undefined;
  return [`${METRIC_LABELS.daysOpen} ${calendar}`, `${SLA_LABELS.activeTime} ${active}`, `${SLA_LABELS.daysOnHold} ${hold}`].join(" · ");
}

/** The card's health and readiness from the widget data alone (drivers only where the data carries them, as the HM's own
 * open reqs do; otherwise they come with the signal). */
function DataSignals({ row, drivers }: { row: ActiveRequisitionRow; drivers?: readonly HealthDriver[] | undefined }) {
  return (
    <Flex gap={10}>
      {row.health_score !== null && row.health_light !== null
        ? <Health size="sm" score={row.health_score} band={row.health_light} drivers={drivers ? [...drivers] : undefined} />
        : null}
      {row.readiness_percent !== null
        ? <Ring size={28} pct={row.readiness_percent} label={HOME_TEMPLATES.searchReadinessPct({ pct: figure(row.readiness_percent, "percent") })} />
        : null}
    </Flex>
  );
}

/** What a row may carry beyond B10's own data: the strip's stations and Days open (B1's own_open_reqs carry both). */
export interface RequisitionFacts {
  stations?: readonly MilestoneStation[] | undefined;
  days?: DaysOpen | undefined;
}

export interface RequisitionCardProps extends RequisitionFacts {
  row: ActiveRequisitionRow;
  signal?: SignalRow | undefined;
  /** Draw the DC-31 stage counts (default). */
  stages?: boolean | undefined;
  /** The Req health drivers the data carries (the HM's own open reqs), named in the ring. */
  drivers?: readonly HealthDriver[] | undefined;
  hsPrefix?: string | undefined;
  /** What the host lists under the card's foot (the HM's drivers that cost points and the missing prerequisites). */
  children?: ReactNode;
}

/** One requisition's card (DC-53): title, id and status; Req health and Search Readiness; the station strip; the counts per
 * board column; the Days open foot line; then what the host lists under it. */
export function RequisitionCard({
  row, signal, stages = true, drivers, stations, days, hsPrefix = "dash.active", children,
}: RequisitionCardProps) {
  const status = row.status ? REQUISITION_STATUS_WORDS[row.status] : undefined;
  const foot = days ? daysLine(days) : undefined;
  return (
    <Card2 tight>
      <Stack gap={8}>
        <Row cols={2} lead="60%" href={row.route || undefined} hs={hsKey(hsPrefix, "req", row.requisition_id)}>
          <RowName name={row.title} sub={row.requisition_id} />
          {status ? <Pill>{status}</Pill> : null}
        </Row>
        {signal
          ? <RowSignals signal={signal} size="md" hs={hsKey(hsPrefix, "signals", row.requisition_id)} />
          : <DataSignals row={row} drivers={drivers} />}
        {stations && stations.length > 0 ? (
          <MilestoneTimeline
            sm
            label={HISTORY_LABELS.requisitionTimeline}
            stations={stations}
            holdDays={row.status === "on_hold" ? days?.hold_days ?? undefined : undefined}
          />
        ) : null}
        {stages ? (
          <Flex gap={14} wrap>
            {STAGE_COLUMNS.map(([column, word]) => <Stat key={column} label={word} value={row.stage_counts[column] ?? 0} />)}
          </Flex>
        ) : null}
        {foot !== undefined ? <Paragraph sm>{foot}</Paragraph> : null}
        {children}
      </Stack>
    </Card2>
  );
}

export interface RequisitionCardsProps {
  rows: readonly ActiveRequisitionRow[];
  /** Per requisition id, the stations and Days open its row carries. */
  facts?: ReadonlyMap<string, RequisitionFacts> | undefined;
  stages?: boolean | undefined;
  hsPrefix?: string | undefined;
}

export function RequisitionCards({ rows, facts, stages = true, hsPrefix }: RequisitionCardsProps) {
  const signals = useSignals(rows.map((row) => row.requisition_id));
  return (
    <Stack gap={14}>
      {rows.map((row) => (
        <RequisitionCard
          key={row.requisition_id}
          row={row}
          signal={signals.get(row.requisition_id)}
          stages={stages}
          hsPrefix={hsPrefix}
          {...facts?.get(row.requisition_id)}
        />
      ))}
    </Stack>
  );
}

/** The stations and Days open each row of the widget data carries, by requisition id. */
export function factsOf(data: Record<string, unknown>): Map<string, RequisitionFacts> {
  const rows: unknown[] = Array.isArray(data.rows) ? data.rows : [];
  return new Map(rows.flatMap((row) => {
    const loose = (row && typeof row === "object" ? row : {}) as Record<string, unknown>;
    return typeof loose.requisition_id === "string"
      ? [[loose.requisition_id, { stations: stationsOf(loose.stations), days: daysOf(loose) }] as const]
      : [];
  }));
}

// D-25 (UAT3 floor ruling 05:42): a widget home.open composes is drawn even with nothing in it, its Card's empty
// slot holding B10's DASH (DS §2.1 empty → EmptyState; PLAN D2 "nothing measured" is "—"), until he words the line
// (Q-E19 "What should the empty-state line say?").
/** Widget `active_requisitions` (S13 → S16, frozen; held on the server until C-B10-8, defined by B1 since D-25). */
export const ActiveRequisitions: HomeWidgetView = ({ widgetKey, data }) => {
  const active = activeRequisitionsOf(data);
  const none = active.rows.length === 0;
  return (
    <Widget
      id={widgetKey}
      kind="sections"
      roles={seatsOf(widgetKey)}
      title={NAV_ITEM_LABELS.requisitions}
      sub={active.scope_words.length > 0 ? active.scope_words.join(" · ") : undefined}
      count={active.rows.length}
      empty={none ? DASH : undefined}
    >
      {none ? null : <RequisitionCards rows={active.rows} facts={factsOf(data)} />}
    </Widget>
  );
};
